import React, { useState, useCallback, useEffect, useRef } from 'react';

import { track, EVENTS } from '../../../../lib/tracking';
import { useEditState } from '../../../lib/useEditState';
import { sections } from '../../../lib/playerPreferenceMeta';
import { isDebugEnabled } from '../../../lib/debugFlag';
import TextInput from '../../../hoc/types/TextInput';
import Navigation from './Navigation';
import PreferenceTabWrapper from './PreferenceTabWrapper';
import ThemePreview, { ThemePreviewToggle } from './ThemePreview';

/**
 * Player preference editor for the PMM v4 flat shape. Function
 * component consuming `useEditState`; replaces the legacy
 * class + `PreferenceStateUtil` HOC pairing.
 *
 * Save / Reset semantics:
 *   - Reset clears the pending edits bag (undoEdits).
 *   - Save calls `actions.handleSave(merged, cb)`. On success it
 *     also undoes the edits bag -- the store will repopulate from
 *     the response, and stale edits would otherwise re-mark the
 *     editor dirty.
 *   - Save is disabled when there are no edits, or when `loadFailed`
 *     is true (Phase 4 guard rail -- prevents persisting
 *     freshDefaults into the prefID slot the user thought they
 *     were editing).
 */
export default function PlayerPreferenceEditor (props) {
    const {
        preference,
        preferenceID,
        clientID,
        loadFailed,
        assets,
        peerStatus,
        actions,
        onWorkspaceAction,
    } = props;

    const { edits, merged, storeEdits, undoEdits } = useEditState(preference);
    const [activeTabKey, setActiveTabKey] = useState(sections[0].key);
    // The preview lives at the editor ROOT, not inside the Theme tab, for
    // two reasons that go together:
    //   - Reserve: the title/Reset/Save header is a sibling of the tab, so
    //     the drawer-width reserve has to be applied here or that header
    //     sits under the drawer.
    //   - Persistence: rendered here it survives a tab switch and keeps
    //     painting from `merged` regardless of which tab is active, so a
    //     color edit lands live without the preview closing the moment you
    //     leave the Theme tab (which is what happened when it lived inside
    //     the tab and unmounted with it).
    const [previewOpen, setPreviewOpen] = useState(false);
    // What is behind the player. The player paints no background of its own,
    // so this is an assumption -- but it is the user's own assumption, made
    // visible in the preview toolbar and reused by the contrast audit so a
    // warning can say what it measured against instead of asserting a fact
    // Control Center does not have.
    const [darkPage, setDarkPage] = useState(false);
    // Theme takes the editor over: 48 tokens do not fit a column beside a
    // section nav, and the preview stops being an occasional check and
    // becomes the thing you are working against. So on this one tab the nav
    // collapses to a strip, the tab column narrows to a control rail, and
    // the preview is a permanent pane rather than a dismissible overlay.
    //
    // A takeover rather than its own route, deliberately: a route would cost
    // three-place registration (routes/index, routes/permissions,
    // WorkspaceNavigation) and would split the save/dirty model across two
    // screens for a preference that is edited as one object.
    const isThemeMode = activeTabKey === 'theme';

    // Theme mode is an APP SHELL: the workspace fills exactly the viewport it
    // has left, the page itself never scrolls, and the rail and the preview
    // each scroll inside themselves.
    //
    // The height cannot be a constant. It was `calc(100vh - ...)`, which sized
    // the panes as though they owned the viewport while they actually start
    // below the breadcrumb and the title/Save bar -- roughly 420px down. Their
    // bottoms landed off-screen, so the page had to scroll to reach them, and
    // scroll chaining then handed you from the rail to the page mid-gesture.
    //
    // So measure where the workspace actually begins and publish it. The bar
    // above is not a fixed height either -- a validation error grows it -- so
    // this re-measures on resize and on anything that changes that bar.
    const bodyRef = useRef(null);
    useEffect(() => {
        const el = bodyRef.current;
        if (!isThemeMode || !el) return undefined;

        const measure = () => {
            // Document-relative, so a page that is still scrolled when this
            // runs does not report a top short by the scroll offset.
            const top = el.getBoundingClientRect().top + window.pageYOffset;
            el.style.setProperty('--theme-shell-top', Math.max(0, Math.round(top)) + 'px');
        };
        measure();

        window.addEventListener('resize', measure);
        // The header can change height without the window resizing.
        let observer = null;
        if (typeof ResizeObserver === 'function' && el.parentNode) {
            observer = new ResizeObserver(measure);
            observer.observe(el.parentNode);
        }
        return () => {
            window.removeEventListener('resize', measure);
            if (observer) observer.disconnect();
        };
    }, [isThemeMode, validators]);
    const [validators, setValidators] = useState([]);
    // Transient "Copied!" acknowledgement for the raw-JSON copy button.
    const [copied, setCopied] = useState(false);
    // Debug affordances (Copy JSON button + window handle) are off in the
    // normal admin UI and revealed by the sticky `?debug` flag -- or always
    // in a development build. Read once per mount: the flag is sticky, so a
    // re-read on every render would just repeat the same answer.
    const debugMode = useState(isDebugEnabled)[0];

    // Console handle on the live pref (saved base + pending edits), so the
    // exact JSON being edited can be inspected or copied straight from the
    // console: `copy(window.__PLAYER_PREF)`. Gated on debug mode (always on
    // in dev) and dropped on unmount so it cannot go stale.
    useEffect(() => {
        if (!debugMode) return undefined;
        window.__PLAYER_PREF = merged;
        return () => { delete window.__PLAYER_PREF; };
    }, [merged, debugMode]);

    const handleCopyJSON = useCallback(() => {
        const json = JSON.stringify(merged, null, 2);
        // Guard: clipboard API is absent on insecure origins (non-localhost
        // http). Fall back to selecting nothing loud -- just log the JSON so
        // it is still retrievable, rather than throwing on a dead promise.
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            console.warn('Clipboard unavailable; preference JSON:\n', json);
            return;
        }
        navigator.clipboard.writeText(json).then(() => {
            setCopied(true);
        }, (err) => {
            console.warn('Clipboard write failed; preference JSON:\n', json, err);
        });
    }, [merged]);

    // Drop the "Copied!" label back to "Copy JSON" after a moment.
    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleNameChange = useCallback((value) => {
        storeEdits('name', value);
    }, [storeEdits]);

    const isDirty = Object.keys(edits).length > 0;

    const handleSave = useCallback((ev) => {
        if (ev) ev.preventDefault();

        if (!merged.name) {
            setValidators(['name']);
            return;
        }
        setValidators([]);

        actions.handleSave(merged, function (err) {
            if (err) {
                console.warn(err);
                onWorkspaceAction({
                    type: 'snack',
                    snack: { type: 'error', message: 'Failed to Save' },
                });
            } else {
                onWorkspaceAction({
                    type: 'snack',
                    snack: { type: 'success', message: 'Settings Saved' },
                });
                undoEdits();
                track(EVENTS.PLAYER_PREF_SAVE, { is_new: !preferenceID });
            }
        });
    }, [merged, actions, onWorkspaceAction, undoEdits, preferenceID]);

    const nameClass = [
        'input-group',
        validators.indexOf('name') !== -1 ? 'has-error' : null,
    ].filter(Boolean).join(' ');

    const saveDisabled = !isDirty || loadFailed;

    return (
        <div className={'player-pref-editor' +
            (previewOpen && !isThemeMode ? ' player-pref-editor--preview-open' : '') +
            (isThemeMode ? ' player-pref-editor--theme' : '')}>
            <header className="row">
                <div className="col-sm-12">
                    <div className="callout">
                        <div className="col-md-6">
                            <div className={nameClass}>
                                <span className="input-group-addon">
                                    Preference Title
                                </span>
                                <TextInput
                                    placeholder="Required"
                                    value={merged.name}
                                    storeEdits={handleNameChange}
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="player-pref-editor__actions">
                                {/* Preview toggle lives here, beside Save, so
                                    it is reachable from every tab that has a
                                    dismissible preview. Theme mode has none:
                                    the preview is a permanent pane there, so
                                    a toggle would have nothing to say. */}
                                {!isThemeMode ? (
                                    <ThemePreviewToggle
                                        isOpen={previewOpen}
                                        onOpenChange={setPreviewOpen}
                                    />
                                ) : null}
                                {/* Copies the live pref -- saved base plus
                                    any pending edits -- as pretty-printed
                                    JSON, for pasting into a ticket or diff.
                                    A debug affordance: hidden in the normal
                                    admin UI, shown in dev or under `?debug`. */}
                                {debugMode ? (
                                    <button type="button"
                                        onClick={handleCopyJSON}
                                        className="btn btn-link"
                                        title="Copy this preference as JSON">
                                        <i className={'fa ' + (copied ? 'fa-check' : 'fa-clipboard')} />
                                        &nbsp;{copied ? 'Copied!' : 'Copy JSON'}
                                    </button>
                                ) : null}
                                <button type="button"
                                    disabled={!isDirty}
                                    onClick={undoEdits}
                                    className="btn btn-link">
                                    Reset
                                </button>
                                <button type="button"
                                    disabled={saveDisabled}
                                    onClick={handleSave}
                                    className="btn btn-primary">
                                    <i className="fa fa-save" />
                                    &nbsp;{preferenceID ? 'Save' : 'Create'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <br/>

            <div
                className={'row player-pref-editor__body' +
                    (isThemeMode ? ' player-pref-editor__body--theme' : '')}
                id="player-preference-editor"
                ref={bodyRef}
            >
                <div className="col-md-2 player-pref-editor__nav">
                    <Navigation
                        activeTabKey={activeTabKey}
                        onSelect={setActiveTabKey}
                        collapsed={isThemeMode}
                    />
                </div>
                <div className="col-md-10 player-pref-editor__main">
                    <PreferenceTabWrapper
                        sectionKey={activeTabKey}
                        merged={merged}
                        storeEdits={storeEdits}
                        assets={assets}
                        peerStatus={peerStatus}
                        prefID={preferenceID}
                        clientID={clientID}
                        isDirty={isDirty}
                        actions={actions}
                        onWorkspaceAction={onWorkspaceAction}
                        darkPage={darkPage}
                    />
                </div>

                {/* ONE position in the tree for both shells. In drawer mode
                    it is position:fixed, so sitting inside the body row costs
                    it nothing; in pane mode it takes the row's third column.
                    Keeping it here across the switch is what stops the
                    <iframe> being re-parented -- which would unmount it and
                    reboot the player on every mode change. */}
                <ThemePreview
                    preference={merged}
                    prefID={preferenceID}
                    mode={isThemeMode ? 'pane' : 'drawer'}
                    isOpen={previewOpen}
                    onOpenChange={setPreviewOpen}
                    darkPage={darkPage}
                    onDarkPageChange={setDarkPage}
                />
            </div>
        </div>
    );
}
